// Правка шаблона и тренировки словами: подписи правок для чата и история разговора для ИИ.

import { formatNumber, pluralRu } from '@/lib/format'

import type { EditOperation, EditReply, EditTurn, WorkoutSuggestion } from './api'
import { exerciseTargets, shortDuration } from './logic'

/** Сообщение чата правки: просьба пользователя или ответ ИИ с правками. */
export type EditMessage =
  | { id: string; role: 'user'; text: string }
  | {
      id: string
      role: 'assistant'
      reply: EditReply
      applied: boolean
      /** Названия упражнений на момент просьбы: упражнение могли уже убрать, а в ответе оно есть. */
      names: Record<string, string>
    }

/** Название упражнения по ключу клиента (могло уже исчезнуть — тогда «упражнение»). */
export type NameOf = (key: string | null) => string

function where(after: string | null, nameOf: NameOf): string {
  if (after === 'start') return 'в начало'
  if (after === null) return 'в конец'
  return `после «${nameOf(after)}»`
}

/** «4 подхода · 10 повт. · 60 кг · отдых 1,5 мин» — что меняет `update`. */
function changes(op: EditOperation): string {
  const parts: string[] = []
  if (op.sets !== null) parts.push(`${op.sets} ${pluralRu(op.sets, ['подход', 'подхода', 'подходов'])}`)
  if (op.reps !== null) parts.push(`${op.reps} повт.`)
  if (op.weight_kg !== null) parts.push(`${formatNumber(op.weight_kg)} кг`)
  if (op.duration_s !== null) parts.push(shortDuration(op.duration_s))
  if (op.distance_m !== null) parts.push(`${formatNumber(op.distance_m)} м`)
  if (op.rest_s !== null) parts.push(`отдых ${shortDuration(op.rest_s)}`)
  return parts.join(' · ') || 'без изменений'
}

/** Знак и текст правки для списка в чате. */
export function describeOp(op: EditOperation, nameOf: NameOf): { sign: string; text: string } {
  const ex = op.exercise
  const name = ex ? (ex.exercise_name ?? ex.name) : ''
  switch (op.op) {
    case 'add':
      return { sign: '+', text: `${name} · ${exerciseTargets(ex!)} · ${where(op.after, nameOf)}` }
    case 'remove':
      return { sign: '−', text: nameOf(op.key) }
    case 'replace':
      return { sign: '⇄', text: `${nameOf(op.key)} → ${name} · ${exerciseTargets(ex!)}` }
    case 'update':
      return { sign: '✎', text: `${nameOf(op.key)}: ${changes(op)}` }
    case 'move':
      return { sign: '↕', text: `${nameOf(op.key)} — ${where(op.after, nameOf)}` }
  }
}

/** Есть упражнение не из справочника — сначала заменить или убрать. */
export function unresolved(ops: EditOperation[]): boolean {
  return ops.some((o) => o.exercise !== null && o.exercise.exercise_id === null)
}

/** Подставить упражнение из подсказок вместо незнакомого. */
export function pickSuggestion(reply: EditReply, index: number, s: WorkoutSuggestion): EditReply {
  return {
    ...reply,
    operations: reply.operations.map((o, i) =>
      i === index && o.exercise
        ? { ...o, exercise: { ...o.exercise, exercise_id: s.id, exercise_name: s.name, tracking: s.tracking, suggestions: [] } }
        : o,
    ),
  }
}

export function dropOperation(reply: EditReply, index: number): EditReply {
  return { ...reply, operations: reply.operations.filter((_, i) => i !== index) }
}

/** Разговор для ИИ: просьбы и ответы словами, у ответа — применены ли правки. */
export function editHistory(messages: EditMessage[]): EditTurn[] {
  return messages.map((m) =>
    m.role === 'user'
      ? { role: 'user', content: m.text }
      : {
          role: 'assistant',
          content: `${m.reply.reply}\n(${
            m.reply.operations.length === 0
              ? 'правок не было'
              : m.applied
                ? 'пользователь применил правки'
                : 'пользователь правки не применил'
          })`,
        },
  )
}
